// supervisor-proposal-batch-persistence.ts
//
// The write half of supervisor-proposal-application.ts (split for the
// 200-line budget, phase 06e): once a proposal batch passed every in-memory
// check (apply + remap + DAG), write each changed ticket file and journal one
// intent/result receipt per change. Receipt operation ids are what makes a
// crash-then-replay skip an already-applied change.
//
// 06e R3 (B4): a synthetic `supersede` of a blocked plan commits the plan's
// `ticket_state_changed` in the SAME batch as its receipt. A replay skips a
// receipted op before it is ever marked touched, so a status left for the
// caller's later shared commit would be lost forever on a crash in between.
import type { MissionStore } from "./mission-store.js";
import type { ProposedChange as ApplierChange } from "./proposal-applier.js";
import type { Ticket } from "./schemas/ticket-schema.js";
import { writeTicketFile } from "./ticket-file-persistence.js";

export interface PendingProposalWrite {
  operationId: string;
  index: number;
  change: ApplierChange;
  /** Set only for the controller's own blocked-plan supersede (06e R3). */
  synthetic?: { planId: string; notice: string };
}

export async function persistProposalBatch(input: {
  store: MissionStore;
  repoDir: string;
  runId: string;
  /** Tickets after the whole batch + remap (created ids already assigned). */
  tickets: readonly Ticket[];
  pendingWrites: readonly PendingProposalWrite[];
  notify?: (line: string) => void;
}): Promise<void> {
  for (const { operationId, index, change, synthetic } of input.pendingWrites) {
    // Read back from `tickets` (not `change.new_ticket`): the caller's H6
    // remap replaced created tickets there.
    const writtenId =
      change.op === "create"
        ? change.new_ticket?.id
        : change.op === "amend"
          ? change.ticket_id
          : undefined;
    const ticket = input.tickets.find((t) => t.id === writtenId);
    if (ticket) await writeTicketFile(input.repoDir, ticket);

    const params = synthetic
      ? { run_id: input.runId, index, op: change.op, synthetic: true, plan_id: synthetic.planId }
      : { run_id: input.runId, index, op: change.op };
    const statusEvent = synthetic
      ? [
          {
            event_id: `${operationId}-status`,
            delta: {
              kind: "ticket_state_changed" as const,
              ticket: synthetic.planId,
              status: "superseded",
            },
          },
        ]
      : [];
    await input.store.commit([
      {
        event_id: `${operationId}-intent`,
        operation_id: operationId,
        delta: {
          kind: "operation_intent",
          operation_id: operationId,
          operation_kind: "apply_proposal",
          params,
        },
      },
      {
        event_id: `${operationId}-result`,
        operation_id: operationId,
        delta: { kind: "operation_result", operation_id: operationId, outcome: "applied" },
      },
      ...statusEvent,
    ]);
    if (synthetic) input.notify?.(synthetic.notice);
  }
}
